import { useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Camera, X, Loader2, Image as ImageIcon } from "lucide-react";

/**
 * Upload de fotos para OS (entrada/saída).
 * Props:
 *  - osId: id da OS (obrigatório)
 *  - kind: 'entry' | 'exit'
 *  - photos: array de urls (relativas /api/static/...)
 *  - onChange: (newPhotosArray) => void
 *  - max: número máximo (default 6)
 */
export default function OsPhotoUploader({
  osId,
  kind,
  photos = [],
  onChange,
  max = 6,
  label,
}) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!osId) {
      toast.error("Salve a OS antes de anexar fotos.");
      return;
    }
    const remaining = max - photos.length;
    const list = Array.from(files || []).slice(0, remaining);
    if (list.length === 0) return;
    setUploading(true);
    let updated = photos;
    for (const file of list) {
      try {
        // Downscale using canvas para reduzir tamanho antes de enviar
        const base64 = await downscaleToBase64(file, 1280, 0.82);
        const { data } = await api.post(`/service-orders/${osId}/photos`, {
          kind,
          image_base64: base64,
        });
        updated = data[`photos_${kind}`] || [...updated, data.url];
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Falha ao enviar foto");
      }
    }
    onChange && onChange(updated);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (url) => {
    if (!window.confirm("Remover essa foto?")) return;
    try {
      const { data } = await api.delete(
        `/service-orders/${osId}/photos?kind=${kind}&url=${encodeURIComponent(url)}`
      );
      onChange && onChange(data[`photos_${kind}`] || []);
    } catch (e) {
      toast.error("Falha ao remover foto");
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u?.startsWith("http") ? u : `${backendUrl}${u}`);

  return (
    <div>
      {label && (
        <label className="block text-xs uppercase tracking-widest text-slate-600 mb-2">
          {label}{" "}
          <span className="text-slate-400 normal-case">
            ({photos.length}/{max})
          </span>
        </label>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {photos.map((u) => (
          <div
            key={u}
            className="relative aspect-square rounded-sm overflow-hidden border border-slate-200 group"
            data-testid={`os-photo-${kind}-item`}
          >
            <img
              src={fullUrl(u)}
              alt="Foto OS"
              className="w-full h-full object-cover"
            />
            {osId && (
              <button
                type="button"
                onClick={() => remove(u)}
                data-testid={`os-photo-${kind}-remove`}
                className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                title="Remover foto"
              >
                <X size={12} />
              </button>
            )}
          </div>
        ))}
        {photos.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || !osId}
            data-testid={`os-photo-${kind}-add`}
            className="aspect-square rounded-sm border-2 border-dashed border-slate-300 hover:border-[#2563EB] hover:bg-blue-50 flex flex-col items-center justify-center gap-1 text-slate-500 disabled:opacity-40 disabled:cursor-not-allowed"
            title={!osId ? "Salve a OS antes de anexar fotos" : "Adicionar foto"}
          >
            {uploading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <>
                <Camera size={20} />
                <span className="text-[10px]">Adicionar</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {!osId && (
        <div className="text-[11px] text-amber-600 mt-2 flex items-center gap-1">
          <ImageIcon size={12} /> Salve a OS primeiro para habilitar as fotos.
        </div>
      )}
    </div>
  );
}

/**
 * Reduz o tamanho da imagem antes do envio para economizar banda/armazenamento.
 */
function downscaleToBase64(file, maxSide = 1280, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl.split(",", 2)[1]); // strip data:image/jpeg;base64,
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
